// Checkout: plans, the pick-one discount list, and payment.
import { useState, useEffect, useRef } from "react";
import { Sparkles, ChevronRight, Check } from "lucide-react";
import { MagicSpinner } from "../../Onboarding.jsx";
import { useLang } from "../../i18n.jsx";
import { api } from "../api.js";
import { fmtDate, planLabel } from "../constants.js";
import { FluentEmoji, Stars } from "../emoji.jsx";
import { fmt, toFa } from "../format.js";
import { DIGI_BLUE, GUARANTEE_DAYS, GUARANTEE_PLANS, SNAPPPAY_ENABLED, SNAPP_BLUE, SUBSCRIPTION_PLANS, planStories } from "../plans.js";
import { clearPendingReferral, getPlatform, messengerName, openExternal, pendingReferral, platformInfo, webApp } from "../platform.js";
import { canOpenInvoice, haptic, openInvoiceInApp, requestWriteAccess } from "../host.js";
import { C, FONT, WHITE, alpha } from "../../design-system.js";
import { BazaarSignInCard, DigiPayLogo, GuaranteeSeal, SnappPayLogo } from "../ui.jsx";
import { bazaarPurchase } from "../android.js";

// ── Subscription Screen ───────────────────────────────────────────────────────
// `onSignIn` is set only in the Bazaar app before «ورود با بازار» — a plan has to
// belong to an account, so the pay button waits for the sign-in card.
// `upgrade` ({plan, options} from GET /subscriptions/upgrade-options) switches the
// screen to UPGRADE mode: only the longer plans, each at the list-price difference,
// no discounts, no installments — it lengthens the running plan, it is NOT a renewal.
export function SubscriptionScreen({ onBack, onPay, childId = null, childName = "", activeSub = null, onSignIn = null, upgrade = null }) {
  const { t, dir } = useLang();
  // No child yet = a plan bought first (step 3 needs no other step); the server
  // hangs it on a placeholder child. With a plan already running, this is a
  // RENEWAL: the new plan is queued to start the moment the current one ends.
  const childQ = childId ? `&child_id=${childId}` : "";
  const [selected,      setSelected]      = useState(null);
  const [discounts, setDiscounts] = useState([]);            // discounts available for the selected plan
  const [selectedDiscount, setSelectedDiscount] = useState(null); // the ONE chosen discount (or null)
  const [codeInput, setCodeInput] = useState("");            // a code the parent types (marketer/gift/…)
  const [codeBusy, setCodeBusy] = useState(false);
  const [codeError, setCodeError] = useState("");
  const [paying, setPaying] = useState(false);
  const [payError, setPayError] = useState(null);
  const [balePending, setBalePending] = useState(false); // invoice sent → awaiting payment
  // "bale" (invoice in the bot chat) | "snapppay" | "zarinpal" — drives the
  // waiting-overlay copy. Everything but "bale" is a HOSTED PAGE in the browser.
  const [payMethod, setPayMethod] = useState("bale");
  const pollRef = useRef(null);
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const isTg = getPlatform() === "telegram";
  // Can this parent pay AT ALL? The backend answers for both routes: an in-app rail
  // (Bale wallet / Telegram Stars) or an external gateway standing in for a
  // messenger that has none. Free/100%-off redemption always works regardless.
  const noPayment = platformInfo().supports_payment === false;
  // Non-null ("zarinpal") when this messenger has NO in-app rail and pays through a
  // hosted page instead — Eitaa and Soroush Plus. Drives the button copy, since
  // «کیف پول {app}» would be a lie there: the money goes through a bank gateway.
  const webPayment = platformInfo().web_payment || null;
  // The Bazaar app pays through Bazaar's own purchase sheet (Poolakey).
  const storeBilling = platformInfo().payment === "bazaar_iab";
  // «پرداخت قسطی با دیجی‌پی» — offered on EVERY messenger once the server has the
  // merchant credentials (platform_info.installment === "digipay"). It needs the
  // payer's mobile number, asked for inline and remembered on this device.
  const installment = platformInfo().installment || null;
  // Telegram prices in Stars, so a Toman installment makes no sense there.
  const canInstall = installment === "digipay" && !isTg && !upgrade;
  const upOpt = (p) => upgrade?.options?.find(o => o.plan_months === p.months) || null;
  // This host shows ads to the free version (Adly — Eitaa), so a plan removes them.
  const hasAds = !!platformInfo().ads;
  const plans = upgrade ? SUBSCRIPTION_PLANS.filter(p => upOpt(p)) : SUBSCRIPTION_PLANS;
  const [digiOpen, setDigiOpen] = useState(false);
  const [phone, setPhone] = useState(() => { try { return localStorage.getItem("zg_phone") || ""; } catch { return ""; } });
  const phoneDigits = phone.replace(/[^0-9۰-۹٠-٩]/g, "").length;   // Persian digits count too
  const plan = selected ? SUBSCRIPTION_PLANS.find(p => p.id === selected) : null;

  // The user's AVAILABLE discounts for this child + plan (renewal, banked invites,
  // exclusive admin codes, and a friend's code via ?ref=). They pick ONE — never
  // stack. Re-fetched whenever the plan changes (percentages are per-plan).
  useEffect(() => {
    setSelectedDiscount(null);
    setCodeError("");
    if (!plan || upgrade) { setDiscounts([]); return; }
    // ?ref=CODE on the web, or startapp=ref_CODE off the host's own deep link
    const ref = pendingReferral();
    api(`/subscriptions/available-discounts?plan_months=${plan.months}${childQ}${ref ? `&ref=${encodeURIComponent(ref)}` : ""}`)
      .then(d => setDiscounts(d.discounts || []))
      .catch(() => setDiscounts([]));
  }, [selected]);

  // A code the parent TYPED (a marketer's campaign code, a gift code from the bot,
  // a renewal or a friend's code). The backend resolves what kind it is and returns
  // one entry shaped exactly like the list above, so it just joins the pick-one list
  // — already selected, since typing it IS the choice.
  async function applyCode() {
    const code = codeInput.trim();
    if (!plan || !code || codeBusy) return;
    setCodeBusy(true);
    setCodeError("");
    try {
      const d = await api(`/subscriptions/check-code?code=${encodeURIComponent(code)}&plan_months=${plan.months}${childQ}`);
      setDiscounts(list => list.some(x => x.kind === d.kind && x.code === d.code) ? list : [...list, d]);
      setSelectedDiscount(d);
      setCodeInput("");
    } catch (e) {
      setCodeError(e.message || t("این کد معتبر نیست"));
    } finally {
      setCodeBusy(false);
    }
  }

  const totalDiscount = selectedDiscount ? selectedDiscount.percent : 0;
  const isFree = totalDiscount >= 100;
  const baseAmount = (p) => isTg ? p.stars : p.price;
  const origAmount = (p) => isTg ? p.starsOriginal : p.originalPrice;
  const starsWithDiscount = (p, percent) => percent >= 100 ? 0
    : Math.max(1, Math.round((p.starsOriginal || p.stars) * (1 - percent / 100)));
  const finalPrice = !plan ? 0 : upOpt(plan) ? (isTg ? upOpt(plan).stars : upOpt(plan).price)
    : !selectedDiscount ? baseAmount(plan)
    : isTg ? starsWithDiscount(plan, totalDiscount) : selectedDiscount.price;
  const priceText = (v) => isTg ? `${toFa(v)} ⭐` : t("{n} تومان", { n: fmt(v) });
  // Installments (DigiPay) pay the PURE duration price: the 2-/3-month list discount
  // is a reward for paying up front. A picked discount CODE is already priced on that
  // pure base server-side, so it carries over unchanged (routers/subscriptions.py).
  const installPrice = !plan ? 0 : selectedDiscount ? finalPrice : (plan.originalPrice || plan.price);
  const unitLabel = isTg ? t("ستاره") : t("تومان");

  async function handlePay(plan, method = "wallet") {
    if (paying) return;
    setPaying(true);
    setPayError(null);
    haptic("medium");
    // Every story is delivered as a bot DM, and on some hosts a DM to a user who
    // never granted write access is simply refused. Opening the Mini App usually
    // grants it implicitly — "usually" is not good enough for the thing the parent
    // is about to pay for, so ask explicitly here, at the one moment it is
    // obviously justified. A refusal (or a host with no such prompt) must NOT block
    // the purchase: the app itself still shows every story.
    try { await requestWriteAccess(); } catch (_) {}
    try {
      const body = { child_id: childId, plan_months: plan.months };
      if (upgrade) body.upgrade_of = upgrade.plan.id;
      if (method) body.payment_method = method;
      if (method === "digipay") {
        body.phone = phone.trim();
        try { localStorage.setItem("zg_phone", body.phone); } catch { /* private mode */ }
      }
      // Tell the server whether this host can open a payment sheet over the app.
      // On Bale that swaps the "invoice lands in your bot chat, go find it" detour
      // for an in-place sheet. The server issues ONE invoice or the other, never
      // both, so this flag has to be honest — hence the capability probe.
      body.can_open_invoice = canOpenInvoice();
      // Exactly one selected discount (the user can't stack them).
      const d = upgrade ? null : selectedDiscount;
      if (d) {
        if (d.kind === "renewal") body.renewal_code = d.code;
        else if (d.kind === "credit") body.redeem_referral = true;
        else if (d.kind === "admin") body.admin_code = d.code;
        else if (d.kind === "referral") body.referral_code = d.code;
        else if (d.kind === "marketer") body.marketer_code = d.code;
        else if (d.kind === "public") body.public_discount_id = d.id;
      }
      const res = await api("/subscriptions", {
        method: "POST",
        body: JSON.stringify(body),
      });
      // A redeemed FREE plan activates immediately (no payment) on either platform.
      if (res?.status === "active") {
        clearPendingReferral(); onPay(plan);
        setPaying(false);
        return;
      }
      // Bazaar app: Bazaar's purchase sheet (native, Poolakey). Its result is the
      // CLIENT's word, so the token goes to the server, which asks BAZAAR before the
      // plan activates (/payment/bazaar/verify). A dropped verify is retried on the
      // next launch from Bazaar's unconsumed purchases (MiniApp recoverBazaarPurchases).
      if (res?.platform === "bazaar" && res?.status === "pending" && res.bazaar) {
        setPayMethod("bazaar");
        setBalePending(true);
        try {
          const p = await bazaarPurchase(res.bazaar);
          await api("/payment/bazaar/verify", { method: "POST", body: JSON.stringify({
            subscription_id: res.subscription_id, purchase_token: p.purchaseToken, sku: p.productId || res.bazaar.sku,
            original_json: p.originalJson || null, signature: p.dataSignature || null }) });
          setBalePending(false); setPaying(false); clearPendingReferral(); onPay(plan);
        } catch (e) {
          setBalePending(false); setPaying(false);
          if (!e?.cancelled) setPayError(e?.message || t("پرداخت ناموفق بود. دوباره تلاش کنید."));
        }
        return;
      }
      // Telegram: open the in-app Stars payment sheet. The subscription is still
      // "pending" — it activates via the successful_payment webhook. The UI only
      // advances once Telegram's callback reports "paid".
      if (res?.platform === "telegram") {
        const tg = webApp();
        if (res.invoice_link && tg?.openInvoice) {
          tg.openInvoice(res.invoice_link, (status) => {
            setPaying(false);
            if (status === "paid") { clearPendingReferral(); onPay(plan); }
            else if (status === "failed") setPayError(t("پرداخت ناموفق بود. دوباره تلاش کنید."));
            // "cancelled" / "pending" → stay on the screen, no error
          });
          return;   // the invoice callback owns the rest of the flow
        }
        setPaying(false);
        setPayError(t("پرداخت تلگرام در دسترس نیست"));
        return;
      }
      // Bale → one invoice exists, either IN-APP (invoice_link, opened over the app)
      // or in the user's bot chat. Both activate via the successful_payment webhook,
      // so either way we poll the subscription until it really is active.
      if (res?.platform === "bale" && res?.status === "pending") {
        // Two different waits, two different instructions: the in-app sheet needs no
        // trip to the bot, so it must not tell the parent to go looking for one.
        setPayMethod(res.invoice_link ? "bale_inapp" : "bale");
        setBalePending(true);
        const started = Date.now();
        // The in-app sheet. Its callback is the CLIENT's word, so it never marks the
        // plan paid — the poll below is the authority. It only saves the parent from
        // staring at a «در انتظار پرداخت» overlay after they cancelled or failed.
        if (res.invoice_link) {
          openInvoiceInApp(res.invoice_link, (status) => {
            if (status === "failed" || status === "cancelled") {
              if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
              setBalePending(false); setPaying(false);
              if (status === "failed") setPayError(t("پرداخت ناموفق بود. دوباره تلاش کنید."));
            }
            // paid / pending → let the poll confirm it against the server
          });
        }
        // Poll the SPECIFIC new subscription — NOT «child has an active plan»,
        // which is already true during a queued renewal and would falsely report
        // an unpaid renewal as paid. This one only flips to active on real payment.
        const newSubId = res.subscription_id;
        if (pollRef.current) clearInterval(pollRef.current);
        pollRef.current = setInterval(async () => {
          try {
            const sub = newSubId ? await api(`/subscriptions/by-id/${newSubId}`) : null;
            if (sub && sub.status === "active") {
              clearInterval(pollRef.current); pollRef.current = null;
              setBalePending(false); setPaying(false); clearPendingReferral(); onPay(plan);
            }
          } catch (_) {}
          if (Date.now() - started > 10 * 60 * 1000) {
            clearInterval(pollRef.current); pollRef.current = null;
            setBalePending(false); setPaying(false);
            setPayError(t("پرداخت تکمیل نشد. اگر مبلغ کسر شده با پشتیبانی تماس بگیرید."));
          }
        }, 3000);
        return;
      }
      // ANY hosted-page gateway (SnappPay, ZarinPal) → open the payment page in the
      // external browser; the sub activates via /payment/{gateway}/callback, so poll
      // the SPECIFIC sub by id. Keyed on `payment_url` rather than on a gateway
      // NAME so a future gateway needs no new branch here — the same reason the
      // backend asks the registry a capability question instead of naming platforms.
      if (res?.payment_url && res?.status === "pending") {
        setPayMethod(res.platform || "snapppay");
        setBalePending(true);
        const started = Date.now();
        const newSubId = res.subscription_id;
        // openExternal, not <a target=_blank> or a bare window.open — these
        // WebViews silently swallow both (see miniapp/platform.js).
        openExternal(res.payment_url);
        if (pollRef.current) clearInterval(pollRef.current);
        pollRef.current = setInterval(async () => {
          try {
            const sub = newSubId ? await api(`/subscriptions/by-id/${newSubId}`) : null;
            if (sub && sub.status === "active") {
              clearInterval(pollRef.current); pollRef.current = null;
              setBalePending(false); setPaying(false); clearPendingReferral(); onPay(plan);
            }
          } catch (_) {}
          if (Date.now() - started > 15 * 60 * 1000) {
            clearInterval(pollRef.current); pollRef.current = null;
            setBalePending(false); setPaying(false);
            setPayError(t("پرداخت تکمیل نشد. اگر مبلغ کسر شده با پشتیبانی تماس بگیرید."));
          }
        }, 3000);
        return;
      }
      // Free / already active → straight through.
      clearPendingReferral(); onPay(plan);
      setPaying(false);
    } catch (e) {
      setPayError(e.message || t("خطا در پرداخت"));
      setPaying(false);
    }
  }

  return (
    <div style={{ minHeight: "100vh", background: C.bg, fontFamily: FONT }}>
      {balePending && (
        <div style={{ position: "fixed", inset: 0, zIndex: 90, background: alpha(C.bgDeep, 0.92), display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
          <div style={{ background: C.bgCard, border: `1px solid ${C.borderGold}`, borderRadius: 22, padding: "28px 24px", maxWidth: 340, textAlign: "center", animation: "popIn 0.25s ease" }}>
            <MagicSpinner size={52} />
            <h3 style={{ color: C.cream, fontSize: 18, fontWeight: 800, margin: "18px 0 8px" }}>{t("در انتظار پرداخت")}</h3>
            <p style={{ color: C.creamDim, fontSize: 14.5, lineHeight: 1.9, margin: "0 0 20px" }}>
              {payMethod === "snapppay"
                ? t("صفحهٔ پرداخت اسنپ‌پی در مرورگر باز شد. پس از پرداخت، به همین‌جا بازگردید؛ اشتراک به‌صورت خودکار فعال می‌شود.")
                : payMethod === "digipay"
                ? t("صفحهٔ پرداخت دیجی‌پی در مرورگر باز شد. روش قسطی را همان‌جا انتخاب کنید؛ پس از پرداخت به همین‌جا بازگردید تا اشتراک خودکار فعال شود.")
                : payMethod === "zarinpal"
                ? t("درگاه پرداخت زرین‌پال در مرورگر باز شد. پس از پرداخت، به همین‌جا بازگردید؛ اشتراک به‌صورت خودکار فعال می‌شود.")
                : payMethod === "bazaar"
                ? t("صفحهٔ پرداخت بازار باز شد. پس از پرداخت، اشتراک به‌صورت خودکار فعال می‌شود.")
                : payMethod === "bale_inapp"
                ? t("صفحهٔ پرداخت باز شد. پس از تکمیل پرداخت، اشتراک به‌صورت خودکار فعال می‌شود.")
                : t("صورت‌حساب پرداخت به گفتگوی شما با ربات ضحا قصه ارسال شد. لطفاً به ربات بازگردید و پرداخت را کامل کنید.")}
            </p>
            {payMethod === "bale" && (
            <button onClick={() => webApp()?.close?.()} className="press"
              style={{ width: "100%", background: `linear-gradient(135deg, ${C.gold}, ${C.goldLight})`, color: C.bgDeep, border: "none", borderRadius: 14, padding: "14px 0", fontFamily: FONT, fontSize: 16, fontWeight: 800, cursor: "pointer", marginBottom: 10 }}>
              {t("رفتن به ربات برای پرداخت")}
            </button>
            )}
            <button onClick={() => { if (pollRef.current) clearInterval(pollRef.current); setBalePending(false); setPaying(false); }} className="press"
              style={{ width: "100%", background: "none", border: `1px solid ${C.border}`, color: C.textMid, borderRadius: 12, padding: "11px 0", fontFamily: FONT, fontSize: 14, cursor: "pointer" }}>
              {t("انصراف")}
            </button>
          </div>
        </div>
      )}
      <div style={{ background: C.bgDeep, padding: "20px 20px 24px", borderBottom: `1px solid ${C.border}`, position: "relative", overflow: "hidden" }}>
        <Stars count={20} />
        <button onClick={onBack} className="press" style={{ background: "none", border: "none", color: C.gold, fontSize: 16, cursor: "pointer", fontFamily: FONT, padding: 0, marginBottom: 16, position: "relative", display: "flex", alignItems: "center", gap: 4 }}>
          <ChevronRight size={18} style={{ transform: dir === "ltr" ? "rotate(180deg)" : "none" }} /> {t("بازگشت")}
        </button>
        <h2 style={{ color: C.cream, fontSize: 22, fontWeight: 800, margin: 0, position: "relative" }}>{upgrade ? t("ارتقای طرح") : activeSub ? t("تمدید اشتراک") : t("اشتراک داستان")}</h2>
        <p style={{ color: C.textMid, fontSize: 15, margin: "6px 0 0", position: "relative" }}>
          {childName ? t("برای {name} — ۲ داستان صوتی در هفته", { name: childName }) : t("۲ داستان صوتی در هفته، مخصوص فرزند شما")}
        </p>
      </div>

      <div style={{ padding: 20 }}>
        {/* Renewing while a plan runs: say plainly that nothing is lost — the new
            plan is queued behind the current one (POST /subscriptions →
            _plan_start_anchor), no gap and no overlap. */}
        {/* Upgrading: lengthen THIS plan, pay only the difference. */}
        {upgrade && (
          <div style={{ display: "flex", gap: 10, alignItems: "flex-start", background: `${C.purple}14`, border: `1px solid ${C.purple}55`, borderRadius: 16, padding: "13px 15px", marginBottom: 16 }}>
            <Sparkles size={18} color={C.purple} style={{ flexShrink: 0, marginTop: 3 }} />
            <div style={{ color: C.cream, fontSize: 14, lineHeight: 1.85 }}>
              {t("طرح فعلی {plan} است. با ارتقا، همین اشتراک بلندتر می‌شود: قصه‌ها و روزهای طرح جدید (با قصه‌های هدیه‌اش) به آن اضافه می‌شود و فقط مابه‌التفاوت قیمت دو طرح را می‌پردازید.",
                { plan: planLabel(upgrade.plan.plan_months) })}
            </div>
          </div>
        )}
        {activeSub && !upgrade && (
          <div style={{ display: "flex", gap: 10, alignItems: "flex-start", background: `${C.teal}12`, border: `1px solid ${C.teal}44`, borderRadius: 16, padding: "13px 15px", marginBottom: 16 }}>
            <Check size={18} color={C.teal} style={{ flexShrink: 0, marginTop: 3 }} />
            <div style={{ color: C.cream, fontSize: 14, lineHeight: 1.85 }}>
              {activeSub.expires_at
                ? t("اشتراک فعلی تا {date} فعال است. طرحی که حالا بخرید، درست بعد از پایان آن شروع می‌شود — بدون فاصله و بدون از دست رفتن حتی یک روز.", { date: fmtDate(activeSub.expires_at) })
                : t("اشتراک فعلی فعال است. طرحی که حالا بخرید، درست بعد از پایان آن شروع می‌شود.")}
            </div>
          </div>
        )}
        {/* Buying before adding a child: honest about what happens next. */}
        {!childId && (
          <div style={{ background: `${C.gold}0F`, border: `1px solid ${C.borderGold}`, borderRadius: 16, padding: "13px 15px", marginBottom: 16, color: C.cream, fontSize: 14, lineHeight: 1.85 }}>
            {t("می‌توانید همین حالا طرح را بخرید و بعد فرزندتان را اضافه کنید و پرسشنامه را پر کنید. قصه‌ها پس از پرسشنامه شروع می‌شوند و روزهای اشتراک هم از همان روز شمرده می‌شود.")}
          </div>
        )}
        {/* Features */}
        <div className="sq-card" style={{ background: C.bgCard, border: `1px solid ${C.borderGold}`, borderRadius: 34, padding: 20, marginBottom: 24 }}>
          {[t("داستان شخصی‌سازی‌شده با نام کودک شما"), t("بر اساس روان‌شناسی و نیاز کودک"), t("۲ داستان صوتی در هفته"), t("ارسال مستقیم در ضحا قصه")].map((f, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "9px 0", borderBottom: i < 3 ? `1px solid ${C.border}` : "none" }}>
              <Sparkles size={17} color={C.gold} />
              <span style={{ color: C.cream, fontSize: 16 }}>{f}</span>
            </div>
          ))}
        </div>

        {/* Installments — said UP FRONT, beside the prices, not only as a second
            button under the pay button where nobody scrolled to find it. */}
        {canInstall && (
          <div style={{ display: "flex", alignItems: "center", gap: 10, background: `${DIGI_BLUE}1F`, border: `1px solid ${DIGI_BLUE}88`, borderRadius: 16, padding: "12px 14px", marginBottom: 14 }}>
            <span style={{ display: "inline-flex", background: DIGI_BLUE, color: WHITE, borderRadius: 10, padding: "6px 8px", flexShrink: 0 }}><DigiPayLogo height={18} /></span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ color: C.cream, fontSize: 14.5, fontWeight: 800 }}>{t("امکان پرداخت قسطی با دیجی‌پی")}</div>
              <div style={{ color: C.textMid, fontSize: 12.5, marginTop: 2, lineHeight: 1.6 }}>{t("همهٔ طرح‌ها را می‌توانید قسطی بخرید — دکمهٔ آبی پایین صفحه.")}</div>
            </div>
          </div>
        )}

        {/* Plans */}
        <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 20 }}>
          {plans.map(plan => (
            <button key={plan.id} onClick={() => setSelected(plan.id)} className="press sq"
              style={{ position: "relative", background: selected === plan.id ? `${plan.color}15` : C.bgCard, border: `2px solid ${selected === plan.id ? plan.color : C.border}`, borderRadius: 18, padding: plan.badge ? "28px 20px 19px" : "19px 20px", display: "flex", alignItems: "center", cursor: "pointer", textAlign: "right", width: "100%", transition: "all 0.2s", boxShadow: selected === plan.id ? `0 0 20px ${plan.color}22` : "none" }}>
              {plan.badge && (
                <span style={{ position: "absolute", top: 8, left: "50%", transform: "translateX(-50%)", background: `linear-gradient(135deg, ${plan.color}, ${C.purple})`, color: WHITE, fontSize: 11.5, fontWeight: 800, padding: "3px 14px", borderRadius: 999, zIndex: 2, whiteSpace: "nowrap", boxShadow: `0 2px 10px ${plan.color}66`, letterSpacing: 0.2 }}>★ {t(plan.badge)}</span>
              )}
              {/* the selection circle sits on the RIGHT — the first thing an RTL eye meets */}
              <div style={{ width: 24, height: 24, borderRadius: "50%", border: `2px solid ${selected === plan.id ? plan.color : C.border}`, background: selected === plan.id ? plan.color : "transparent", display: "flex", alignItems: "center", justifyContent: "center", marginLeft: 14, flexShrink: 0, transition: "all 0.2s" }}>
                {selected === plan.id && <Check size={14} color={C.bgDeep} />}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 5, flexWrap: "wrap" }}>
                  <span style={{ color: plan.color, fontSize: 18, fontWeight: 800 }}>{planLabel(plan.months)}</span>
                  {plan.discount > 0 && <span style={{ background: `${plan.color}22`, color: plan.color, borderRadius: 999, padding: "3px 11px", fontSize: 13, fontWeight: 700 }}>{t("{n}٪ تخفیف", { n: toFa(plan.discount) })}</span>}
                </div>
                <div style={{ color: C.textMid, fontSize: 14 }}>{upOpt(plan)
                  ? t("{n} داستان صوتی بیشتر", { n: toFa(planStories(plan) - planStories(SUBSCRIPTION_PLANS.find(p => p.months === upgrade.plan.plan_months) || plan)) })
                  : t("{n} داستان صوتی", { n: toFa(plan.months * 8) })}</div>
                {plan.bonus > 0 && (
                  <div style={{ color: plan.color, fontSize: 13.5, fontWeight: 800, marginTop: 3 }}>🎁 {t("+ {n} قصهٔ هدیه", { n: toFa(plan.bonus) })}</div>
                )}
                {/* Ads are the free version's (Adly, Eitaa only) — say a plan removes them. */}
                {hasAds && !upgrade && (
                  <div style={{ color: C.creamDim, fontSize: 13, fontWeight: 700, marginTop: 3 }}>🚫 {t("بدون تبلیغات")}</div>
                )}
                {/* The 5-day money-back guarantee — a cash purchase of the 2-/3-month plan. */}
                {!upgrade && GUARANTEE_PLANS.includes(plan.months) && (
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 6 }}>
                    <GuaranteeSeal size={26} days={GUARANTEE_DAYS} compact />
                    <span style={{ color: C.goldLight, fontSize: 12.5, fontWeight: 700 }}>{t("{n} روز ضمانت بازگشت وجه", { n: toFa(GUARANTEE_DAYS) })}</span>
                  </div>
                )}
              </div>
              {upOpt(plan) ? (
              <div style={{ textAlign: "left", marginRight: 10, flexShrink: 0 }}>
                <div style={{ color: C.creamDim, fontSize: 12, marginBottom: 2 }}>{t("مابه‌التفاوت")}</div>
                <div style={{ color: C.cream, fontSize: 18, fontWeight: 800 }}>{isTg ? `${toFa(upOpt(plan).stars)} ⭐` : fmt(upOpt(plan).price)}</div>
                <div style={{ color: C.textMid, fontSize: 13 }}>{unitLabel}</div>
              </div>
              ) : (
              <div style={{ textAlign: "left", marginRight: 10, flexShrink: 0 }}>
                {origAmount(plan) && (
                  <div style={{ color: C.creamDim, fontSize: 13, textDecoration: "line-through", marginBottom: 2 }}>{isTg ? toFa(origAmount(plan)) : fmt(origAmount(plan))}</div>
                )}
                <div style={{ color: C.cream, fontSize: 18, fontWeight: 800 }}>{isTg ? `${toFa(plan.stars)} ⭐` : fmt(plan.price)}</div>
                <div style={{ color: C.textMid, fontSize: 13 }}>{unitLabel}</div>
              </div>
              )}
            </button>
          ))}
        </div>

        {/* The guarantee in one line under the plans — the seal alone doesn't say
            «cash only», and that is the condition parents need to know first. */}
        {!upgrade && (
          <div style={{ display: "flex", alignItems: "center", gap: 10, color: C.creamDim, fontSize: 13, lineHeight: 1.8, margin: "-6px 2px 18px" }}>
            <GuaranteeSeal size={34} days={GUARANTEE_DAYS} />
            <span>{t("طرح‌های دو و سه ماهه با پرداخت یک‌جا (غیرقسطی) {n} روز ضمانت بازگشت وجه دارند. جزئیات در «قوانین و مقررات».", { n: toFa(GUARANTEE_DAYS) })}</span>
          </div>
        )}

        {/* ── Available discounts — pick ONE (they never stack) + a code field ── */}
        {plan && !upgrade && (
          <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 16, padding: 16, marginBottom: 20 }}>
            {discounts.length > 0 && (
            <div style={{ color: C.textMid, fontSize: 14, marginBottom: 12 }}>{t("تخفیف‌های شما (فقط یکی قابل انتخاب است)")}</div>
            )}
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {discounts.map((d) => {
                const sel = selectedDiscount && selectedDiscount.kind === d.kind && selectedDiscount.code === d.code;
                const dprice = isTg ? starsWithDiscount(plan, d.percent) : d.price;
                return (
                  <button key={`${d.kind}-${d.code || ""}`} onClick={() => setSelectedDiscount(sel ? null : d)} className="press"
                    style={{ background: sel ? `${C.gold}18` : C.bgDeep, border: `1.5px solid ${sel ? C.gold : C.border}`, borderRadius: 12, padding: "12px 14px", display: "flex", alignItems: "center", gap: 10, cursor: "pointer", textAlign: dir === "ltr" ? "left" : "right", fontFamily: FONT }}>
                    <span style={{ width: 22, height: 22, borderRadius: "50%", border: `2px solid ${sel ? C.gold : C.textDim}`, background: sel ? C.gold : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      {sel && <Check size={13} color={C.bgDeep} strokeWidth={3} />}
                    </span>
                    <div style={{ flex: 1 }}>
                      <div style={{ color: sel ? C.gold : C.cream, fontSize: 14, fontWeight: 700 }}>🎁 {d.label} — {t("{n}٪ تخفیف", { n: toFa(d.percent) })}</div>
                      <div style={{ color: C.textMid, fontSize: 12.5, marginTop: 2 }}>
                        {d.percent >= 100 ? t("رایگان می‌شود") : t("قیمت با این تخفیف: {n}", { n: priceText(dprice) })}
                      </div>
                      {/* A public sale ends on a date — saying so is the whole nudge. */}
                      {d.until && <div style={{ color: C.goldLight, fontSize: 12, marginTop: 2 }}>
                        {t("فقط تا {date}", { date: new Date(new Date(d.until).getTime() - 1000).toLocaleDateString(dir === "ltr" ? "en-GB" : "fa-IR", { timeZone: "Asia/Tehran", day: "numeric", month: "long" }) })}
                      </div>}
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Have a code? — a marketer's campaign code, or a gift code from the
                bot. The backend decides what kind it is; a valid one joins the
                list above already selected. */}
            <div style={{ marginTop: discounts.length ? 14 : 0 }}>
              <div style={{ color: C.textMid, fontSize: 14, marginBottom: 8 }}>{t("کد تخفیف دارید؟")}</div>
              <div style={{ display: "flex", gap: 8 }}>
                <input
                  value={codeInput}
                  onChange={e => { setCodeInput(e.target.value); setCodeError(""); }}
                  onKeyDown={e => { if (e.key === "Enter") applyCode(); }}
                  placeholder={t("مثلاً SARA20")}
                  autoCapitalize="characters" autoCorrect="off" spellCheck={false}
                  style={{ flex: 1, minWidth: 0, background: C.bgDeep, border: `1.5px solid ${codeError ? C.red : C.border}`, borderRadius: 12, padding: "12px 14px", color: C.cream, fontSize: 15, outline: "none", boxSizing: "border-box", direction: "ltr", textAlign: dir === "ltr" ? "left" : "right", textTransform: "uppercase" }} />
                <button onClick={applyCode} disabled={!codeInput.trim() || codeBusy} className="press"
                  style={{ background: codeInput.trim() && !codeBusy ? `${C.gold}18` : C.bgDeep, border: `1.5px solid ${codeInput.trim() && !codeBusy ? C.gold : C.border}`, color: codeInput.trim() && !codeBusy ? C.gold : C.textDim, borderRadius: 12, padding: "0 18px", fontSize: 14.5, fontWeight: 700, cursor: codeInput.trim() && !codeBusy ? "pointer" : "not-allowed", fontFamily: FONT, flexShrink: 0 }}>
                  {codeBusy ? t("…") : t("اعمال")}
                </button>
              </div>
              {codeError && <div style={{ color: C.red, fontSize: 12.5, marginTop: 7 }}>{codeError}</div>}
            </div>
          </div>
        )}

        {/* ── Price breakdown (when a discount is selected) ─────────────────── */}
        {plan && selectedDiscount && (
          <div style={{ background: C.bgDeep, border: `1px solid ${C.border}`, borderRadius: 14, padding: "14px 16px", marginBottom: 20, animation: "fadeIn 0.3s ease" }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
              <span style={{ color: C.textMid, fontSize: 14 }}>{t("قیمت پلن")}</span>
              <span style={{ color: C.cream, fontSize: 14 }}>{priceText(origAmount(plan) || baseAmount(plan))}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
              <span style={{ color: C.gold, fontSize: 14 }}>🎁 {selectedDiscount.label} ({t("{n}٪ تخفیف", { n: toFa(totalDiscount) })})</span>
              <span style={{ color: C.gold, fontSize: 14 }}>−{priceText((origAmount(plan) || baseAmount(plan)) - finalPrice)}</span>
            </div>
            <div style={{ height: 1, background: C.border, margin: "10px 0" }} />
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ color: C.cream, fontSize: 16, fontWeight: 800 }}>{t("مبلغ نهایی")}</span>
              <span style={{ color: C.gold, fontSize: 18, fontWeight: 800 }}>{priceText(finalPrice)}</span>
            </div>
          </div>
        )}

        {/* Pay button */}
        {payError && (
          <div style={{ color: C.red, fontSize: 14, marginBottom: 12, textAlign: "center" }}>{payError}</div>
        )}
        {/* Only reachable when the parent's messenger has NEITHER an in-app rail NOR
            the web gateway (i.e. ZarinPal is switched off server-side). A 100%-off
            code still redeems; a paid checkout that cannot complete must never be
            offered, so explain instead of showing a button that always errors. */}
        {onSignIn ? (
          <BazaarSignInCard onSignIn={onSignIn} note={t("برای خرید طرح، ابتدا با حساب بازار وارد شوید. اشتراک به همین حساب وصل می‌شود.")} />
        ) : noPayment && !isFree ? (
          <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 16, padding: "16px 18px", textAlign: "center" }}>
            <FluentEmoji anim="wiggle" size={28} style={{ marginBottom: 8 }}>🚧</FluentEmoji>
            <p style={{ color: C.creamDim, fontSize: 14.5, lineHeight: 1.8, margin: 0 }}>
              {t("پرداخت در {app} هنوز فعال نیست. به‌زودی اضافه می‌شود.", { app: messengerName() })}
            </p>
          </div>
        ) : (
        <button onClick={() => plan && !paying && handlePay(plan)} disabled={paying} className="press celebrate sq"
          style={{ width: "100%", background: plan && !paying ? `linear-gradient(135deg, ${C.gold}, ${C.goldLight})` : C.border, color: plan && !paying ? C.bgDeep : C.textDim, border: "none", borderRadius: 16, padding: "17px 0", fontSize: 17, fontWeight: 800, cursor: plan && !paying ? "pointer" : "not-allowed", fontFamily: FONT, transition: "all 0.3s", boxShadow: plan && !paying ? `0 4px 24px ${C.gold}44` : "none" }}>
          {paying
            ? t("در حال پردازش...")
            : !plan
              ? t("یک طرح را انتخاب کنید")
              : isFree
                ? t("🎁 دریافت اشتراک رایگان")
                : isTg
                  ? t("پرداخت {n} ⭐ با استارز تلگرام", { n: toFa(finalPrice) })
                  : storeBilling
                    ? (totalDiscount > 0 || upgrade ? t("پرداخت {n} تومان با بازار", { n: fmt(finalPrice) }) : t("پرداخت با بازار"))
                  : webPayment
                    // No wallet on this messenger — the money goes through a bank
                    // gateway, so never promise «کیف پول {app}» here.
                    ? (upgrade ? t("پرداخت {n} تومان", { n: fmt(finalPrice) }) : t("پرداخت"))
                    : totalDiscount > 0 || upgrade
                      ? t("پرداخت {n} تومان از کیف پول {app}", { n: fmt(finalPrice), app: messengerName() })
                      : t("پرداخت از طریق کیف پول {app}", { app: messengerName() })}
        </button>
        )}
        {/* SnappPay — installment / pay-later, in the gateway's own brand blue
            (#007DFA, from their official logo) with the «Snapp! Pay» wordmark.
            Gated by SNAPPPAY_ENABLED until the merchant OAuth is confirmed. */}
        {SNAPPPAY_ENABLED && !upgrade && !isTg && !noPayment && !webPayment && plan && !isFree && (
          <button onClick={() => !paying && handlePay(plan, "snapppay")} disabled={paying} className="press sq"
            style={{ width: "100%", marginTop: 10, display: "flex", alignItems: "center", justifyContent: "center", gap: 10, background: SNAPP_BLUE, border: "none", color: WHITE, borderRadius: 16, padding: "14px 0", fontSize: 15.5, fontWeight: 800, cursor: paying ? "not-allowed" : "pointer", opacity: paying ? 0.6 : 1, boxShadow: `0 4px 20px ${SNAPP_BLUE}40` }}>
            <span style={{ whiteSpace: "nowrap" }}>{t("پرداخت اقساطی با")}</span>
            <SnappPayLogo height={28} />
          </button>
        )}
        {/* DigiPay — installment / BNPL / credit on DigiPay's own hosted page.
            Telegram prices in Stars, so a Toman installment makes no sense there. */}
        {canInstall && plan && !isFree && (
          <div style={{ marginTop: 10 }}>
            {!digiOpen ? (
              <button onClick={() => !paying && setDigiOpen(true)} disabled={paying} className="press sq"
                style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 10, background: DIGI_BLUE, border: "none", color: WHITE, borderRadius: 16, padding: "14px 0", fontFamily: FONT, fontSize: 15.5, fontWeight: 800, cursor: paying ? "not-allowed" : "pointer", opacity: paying ? 0.6 : 1, boxShadow: `0 4px 20px ${DIGI_BLUE}40` }}>
                <span style={{ whiteSpace: "nowrap" }}>{t("پرداخت قسطی با")}</span>
                <DigiPayLogo height={24} />
              </button>
            ) : (
              <div style={{ background: C.bgCard, border: `1px solid ${DIGI_BLUE}88`, borderRadius: 16, padding: 16 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, color: C.cream, fontSize: 15, fontWeight: 800, marginBottom: 6 }}>
                  <span>{t("پرداخت قسطی با")}</span>
                  <DigiPayLogo height={20} tile={DIGI_BLUE} chevron={WHITE} />
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", margin: "4px 0 2px" }}>
                  <span style={{ color: C.textMid, fontSize: 13 }}>{t("مبلغ پرداخت قسطی")}</span>
                  <span style={{ color: C.cream, fontSize: 17, fontWeight: 800 }}>{priceText(installPrice)}</span>
                </div>
                {!selectedDiscount && plan.discount > 0 && (
                  <p style={{ color: C.textDim, fontSize: 12, lineHeight: 1.7, margin: "0 0 6px" }}>
                    {t("تخفیف خرید چندماهه فقط برای پرداخت یک‌جاست.")}
                  </p>
                )}
                <p style={{ color: C.textMid, fontSize: 13, lineHeight: 1.8, margin: "0 0 10px" }}>
                  {t("دیجی‌پی برای بررسی اعتبار قسطی، شمارهٔ موبایلِ صاحب حساب را لازم دارد.")}
                </p>
                <input value={phone} onChange={e => setPhone(e.target.value)} inputMode="tel" dir="ltr" placeholder="09121234567"
                  style={{ width: "100%", boxSizing: "border-box", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 12, padding: "12px 14px", color: C.cream, fontFamily: FONT, fontSize: 17, outline: "none", textAlign: "center", letterSpacing: 1 }} />
                <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                  <button onClick={() => !paying && handlePay(plan, "digipay")} disabled={paying || phoneDigits < 10} className="press sq"
                    style={{ flex: 1, background: DIGI_BLUE, border: "none", color: WHITE, borderRadius: 14, padding: "13px 0", fontFamily: FONT, fontSize: 15, fontWeight: 800, cursor: "pointer", opacity: (paying || phoneDigits < 10) ? 0.55 : 1 }}>
                    {paying ? t("در حال پردازش...") : t("ادامه در دیجی‌پی")}
                  </button>
                  <button onClick={() => setDigiOpen(false)} className="press sq"
                    style={{ background: "none", border: `1px solid ${C.border}`, color: C.textMid, borderRadius: 14, padding: "13px 18px", fontFamily: FONT, fontSize: 14, cursor: "pointer" }}>
                    {t("انصراف")}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
        {!noPayment && (
          <p style={{ textAlign: "center", color: C.textDim, fontSize: 13, marginTop: 14, lineHeight: 1.7 }}>
            {isTg
              ? t("پرداخت امن با استارز تلگرام (Telegram Stars)")
              : storeBilling
                ? t("پرداخت امن از طریق بازار")
              : webPayment
                ? t("پرداخت امن از طریق درگاه زرین‌پال · با تمام کارت‌های بانکی")
                : t("پرداخت امن از طریق کیف پول {app} · بدون کارمزد اضافه", { app: messengerName() })}
            {canInstall && <><br />{t("یا پرداخت قسطی با دیجی‌پی")}</>}
          </p>
        )}
      </div>
    </div>
  );
}
